import React, { useState, useRef, useEffect } from 'react'

import Form from 'react-bootstrap/Form'

import { ChartFilter, CurrentChartFilter } from '../types'
import { Modal } from '@/Atomic/Organisms/Modal'

/**
 * Convert chart filters to filter state
 */
function filterToFilterState(filters: ChartFilter[]): CurrentChartFilter[] {
  return filters.map((filter) => {
    return {
      ID: filter.ID,
      CUR_ID: filter.CUR_ID ?? '',
    }
  })
}

interface FilterSelectorModalProps {
  isOpen: boolean
  close: () => void
  confirmationButtonText?: string
  title: string
  filters: ChartFilter[]
  onFilterChange: (filter: CurrentChartFilter[]) => void
}

export const FilterSelectorModal: React.FC<FilterSelectorModalProps> = ({
  isOpen,
  close,
  confirmationButtonText,
  title,
  filters,
  onFilterChange,
}) => {
  const [filtersState, setFiltersState] = useState(filters)
  const prevSelectedFilter = useRef<string>(
    JSON.stringify(filterToFilterState(filters)),
  )

  // Sync internal state when filters prop changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFiltersState(filters)
    prevSelectedFilter.current = JSON.stringify(filterToFilterState(filters))
  }, [filters])

  const checkChangedAndUpdate = () => {
    if (
      prevSelectedFilter.current !==
      JSON.stringify(filterToFilterState(filtersState))
    ) {
      onFilterChange(filtersState)
      prevSelectedFilter.current = JSON.stringify(
        filterToFilterState(filtersState),
      )
    }
  }

  const handleOnChange = (filterId: string, value: string) => {
    const updatedFiltersState = filtersState.map((item) => {
      if (item.ID === filterId) {
        return { ...item, CUR_ID: value }
      }
      return item
    })
    setFiltersState(updatedFiltersState)
  }

  const handleApplyFilters = () => {
    checkChangedAndUpdate()
    close()
  }

  const handleCancel = () => {
    setFiltersState(filters)
    close()
  }

  const filtersElements = filtersState.map((filter) => {
    const filterOptions = filter.OPT.map((option) =>
      option.ID && option.NAM ? (
        <option key={option.ID} value={option.ID}>
          {option.NAM}
        </option>
      ) : null,
    )

    return (
      <Form.Group className="mb-3" key={filter.ID}>
        <Form.Label>{filter.NAM}</Form.Label>
        <Form.Control
          as="select"
          defaultValue={filter.CUR_ID}
          onChange={(e) => handleOnChange(filter.ID, e.target.value)}
        >
          {filterOptions}
        </Form.Control>
      </Form.Group>
    )
  })

  return (
    <Modal isOpen={isOpen} onClose={close} closeOnOutsideClick={false}>
      <Modal.Body>
        <h3 className="pt-4">{title}</h3>
        {filtersElements}
      </Modal.Body>
      <Modal.Footer
        onConfirm={handleApplyFilters}
        onConfirmText={confirmationButtonText}
        onCancel={handleCancel}
      ></Modal.Footer>
    </Modal>
  )
}
